import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Input,
  Menu,
  Select,
  Table,
  Typography,
} from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import { useNavigate } from "react-router-dom";
import useGetCatalog from "../../hooks/getCatalog/useGetCatalog";
import useUpdateCatalog from "../../hooks/updateCatalog/useUpdateCatalog";
import { useProcessContext } from "../../context/ProcessContext";
import { TimeFormatter } from "helpers/TimeFormatter/TimeFormatter";
import ExportTableButton from "layouts/LayoutTableExport/LayoutTableExport";
import CatalogStatusTag from "../../components/CatalogStatusTag/CatalogStatusTag";
import CopyableTruncatedCell from "../../components/CopyableTruncatedCell";
import TableSkeleton from "../../ui/table/TableSkeleton";
import { TABLE_PAGINATION_UX } from "../../ui/table/paginationDefaults";
import type {
  CatalogExportRow,
  CatalogFilterType,
  CatalogParentItem,
  CatalogStatus,
  CatalogVariationItem,
} from "../../types/catalog";
import {
  CATALOG_FILTER_TYPE_OPTIONS,
  CATALOG_FILTER_TYPE_PLACEHOLDERS,
  CATALOG_STATUS_LABELS,
  CATALOG_VARIATIONS_ERROR_MESSAGE,
} from "../../types/catalog";
import { invalidateSessionRequestCachePrefix } from "../../utils/sessionRequestCache";
import "./CatalogPage.css";

const renderVariationCount = (
  count: number,
  record: CatalogParentItem
) => {
  const synced = record.variationsSynced ?? count;
  const isPartial = synced < count;

  return (
    <span
      className={`catalog-page__variation-count ${
        isPartial ? "catalog-page__variation-count--partial" : ""
      }`}
    >
      {synced}/{count}
    </span>
  );
};

const renderBoolean = (value: boolean) => (value ? "Sí" : "No");

const renderNullableId = (value: number | null) =>
  value != null ? String(value) : "—";

const renderCopyable = (
  value: string | null | undefined,
  copySuccessMessage: string
) => {
  if (value == null || value === "" || value === "—") return "—";
  return (
    <CopyableTruncatedCell
      value={String(value)}
      copySuccessMessage={copySuccessMessage}
      onlyWhenTruncated={false}
    />
  );
};

const parentColumnsDef = [
  {
    title: "ID ITEM SIESA",
    dataIndex: "manufactureCode",
    key: "manufactureCode",
  },
  {
    title: "Product ID VTEX",
    dataIndex: "productIdVtex",
    key: "productIdVtex",
  },
  {
    title: "NOMBRE PRODUCTO",
    dataIndex: "nameProduct",
    key: "nameProduct",
  },
  {
    title: "Variaciones Sincronizadas",
    headerTitle: (
      <>
        Variaciones
        <br />
        Sincronizadas
      </>
    ),
    dataIndex: "variationsSynced",
    key: "variationsSynced",
  },
  { title: "Estado", dataIndex: "statusCatalog", key: "statusCatalog" },
  {
    title: "Última Sincronización",
    headerTitle: (
      <>
        Última
        <br />
        Sincronización
      </>
    ),
    dataIndex: "lastSyncDate",
    key: "lastSyncDate",
  },
];

const buildVariationColumns = (): TableColumnsType<CatalogVariationItem> => [
  {
    title: "SKU ID VTEX",
    dataIndex: "skuIdVtex",
    key: "skuIdVtex",
    width: 120,
    render: (value: number | null) =>
      renderCopyable(renderNullableId(value), "SKU VTEX copiado"),
  },
  {
    title: "Reference ID",
    dataIndex: "refId",
    key: "refId",
    width: 120,
    render: (value: string) => renderCopyable(value, "Referencia copiada"),
  },
  {
    title: "NOMBRE PRODUCTO",
    dataIndex: "nameProduct",
    key: "nameProduct",
    width: 160,
    render: (value: string) => renderCopyable(value, "Nombre copiado"),
  },
  {
    title: "Estado",
    dataIndex: "statusCatalog",
    key: "statusCatalog",
    width: 120,
    render: (status: CatalogStatus) => <CatalogStatusTag status={status} />,
  },
  {
    title: "Última Sincronización",
    dataIndex: "lastSyncDate",
    key: "lastSyncDate",
    width: 120,
    render: (date: string | null) => (date ? TimeFormatter(date) : "—"),
  },
];

const exportColumns = [
  { title: "Tipo", key: "tipo" },
  { title: "Product ID", key: "itemId" },
  { title: "Reference ID", key: "refId" },
  { title: "Cód. fabricante", key: "manufactureCode" },
  { title: "Producto", key: "nameProduct" },
  { title: "Product ID VTEX", key: "productIdVtex" },
  { title: "Estado", key: "statusCatalog" },
  { title: "Variaciones Sincronizadas", key: "variationsSynced" },
  { title: "Faltantes", key: "variationsMissing" },
  { title: "Duplicado VTEX", key: "hasDuplicateProduct" },
  { title: "Última Sincronización", key: "lastSyncDate" },
];

const flattenCatalogForExport = (
  products: CatalogParentItem[]
): CatalogExportRow[] => {
  const rows: CatalogExportRow[] = [];

  products.forEach((parent) => {
    rows.push({
      tipo: "Producto padre",
      itemId: parent.itemId,
      refId: "",
      manufactureCode: parent.manufactureCode,
      nameProduct: parent.nameProduct,
      productIdVtex: renderNullableId(parent.productIdVtex),
      statusCatalog: CATALOG_STATUS_LABELS[parent.statusCatalog],
      variationsSynced: parent.variationsSynced,
      variationsMissing: parent.variationsMissing,
      hasDuplicateProduct: renderBoolean(parent.hasDuplicateProduct),
      lastSyncDate: parent.lastSyncDate
        ? TimeFormatter(parent.lastSyncDate)
        : "",
    });

    parent.variations?.forEach((variation) => {
      rows.push({
        tipo: "Variación",
        itemId: parent.itemId,
        refId: variation.refId,
        manufactureCode: variation.manufactureCode,
        nameProduct: variation.nameProduct,
        productIdVtex: renderNullableId(variation.productIdVtex),
        statusCatalog: CATALOG_STATUS_LABELS[variation.statusCatalog],
        variationsSynced: "",
        variationsMissing: "",
        hasDuplicateProduct: "",
        lastSyncDate: variation.lastSyncDate
          ? TimeFormatter(variation.lastSyncDate)
          : "",
      });
    });
  });

  return rows;
};

const VariationsTable: React.FC<{ record: CatalogParentItem }> = ({
  record,
}) => {
  const variationColumns = useMemo(() => buildVariationColumns(), []);

  if (!Array.isArray(record.variations)) {
    return (
      <Typography.Text type="danger" className="catalog-page__variations-error">
        {CATALOG_VARIATIONS_ERROR_MESSAGE}
      </Typography.Text>
    );
  }

  if (record.variations.length === 0) {
    return (
      <Typography.Text type="secondary" className="catalog-page__variations-error">
        {CATALOG_VARIATIONS_ERROR_MESSAGE}
      </Typography.Text>
    );
  }

  return (
    <Table<CatalogVariationItem>
      className="catalog-page__nested-table"
      columns={variationColumns}
      dataSource={record.variations}
      rowKey="refId"
      pagination={false}
      size="small"
    />
  );
};

const CatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const { setLogId, setSelectedProcessType } = useProcessContext();
  const { catalog, loading, error, totalElements, fetchCatalog } =
    useGetCatalog();

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [searchFilterType, setSearchFilterType] =
    useState<CatalogFilterType>("MANUFACTURE_CODE");
  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    "manufactureCode",
    "productIdVtex",
    "nameProduct",
    "variationsSynced",
    "statusCatalog",
    "lastSyncDate",
  ]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const catalogPage = pagination.current || 1;
  const catalogPageSize = pagination.pageSize || 10;

  const refreshCatalog = useCallback(() => {
    invalidateSessionRequestCachePrefix("catalog:");
    fetchCatalog(
      catalogPage,
      catalogPageSize,
      appliedSearch,
      searchFilterType,
      { force: true }
    );
  }, [
    fetchCatalog,
    catalogPage,
    catalogPageSize,
    appliedSearch,
    searchFilterType,
  ]);

  const {
    openSyncModal,
    renderSyncModal,
    syncLoading,
    syncResult,
    clearSyncResult,
  } = useUpdateCatalog({
    onSyncComplete: refreshCatalog,
  });

  const isBusy = loading || syncLoading;

  const handleViewSyncDetail = useCallback(() => {
    if (!syncResult?.logId) return;

    if (syncResult.processTypeId != null) {
      setSelectedProcessType(syncResult.processTypeId);
    }
    setLogId(syncResult.logId);
    navigate(`/logs/${syncResult.logId}`);
  }, [
    navigate,
    setLogId,
    setSelectedProcessType,
    syncResult?.logId,
    syncResult?.processTypeId,
  ]);

  const parentColumns: TableColumnsType<CatalogParentItem> = useMemo(
    () =>
      parentColumnsDef
        .filter((def) => selectedColumns.includes(def.key))
        .map(({ headerTitle, ...def }) => {
          // Table header may force line breaks; the column menu keeps `title`.
          const col = { ...def, title: headerTitle ?? def.title };
          if (col.key === "statusCatalog") {
            return {
              ...col,
              render: (status: CatalogStatus) => (
                <CatalogStatusTag status={status} />
              ),
            };
          }
          if (col.key === "variationsSynced") {
            return {
              ...col,
              render: (_: number, record: CatalogParentItem) =>
                renderVariationCount(record.variationCount, record),
            };
          }
          if (col.key === "productIdVtex") {
            return {
              ...col,
              width: 150,
              onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
              render: (value: number | null) =>
                renderCopyable(renderNullableId(value), "Product ID VTEX copiado"),
            };
          }
          if (col.key === "manufactureCode") {
            return {
              ...col,
              width: 130,
              render: (value: string) =>
                renderCopyable(value, "ID ITEM SIESA copiado"),
            };
          }
          if (col.key === "nameProduct") {
            return {
              ...col,
              width: 180,
              render: (value: string) =>
                renderCopyable(value, "Nombre copiado"),
            };
          }
          if (col.key === "lastSyncDate") {
            return {
              ...col,
              render: (date: string | null) =>
                date ? TimeFormatter(date) : "—",
            };
          }
          return col;
        }),
    [selectedColumns]
  );

  const catalogData = useMemo(() => catalog ?? [], [catalog]);

  const exportData = useMemo(
    () => flattenCatalogForExport(catalogData),
    [catalogData]
  );

  const handleColumnSelection = (columnKey: string, checked: boolean) => {
    setSelectedColumns((prev) =>
      checked ? [...prev, columnKey] : prev.filter((key) => key !== columnKey)
    );
  };

  const handleTableChange = (nextPagination: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current,
      pageSize: nextPagination.pageSize,
    }));
  };

  // Same as Orders: search only runs on Enter / button, not while typing.
  const submitSearch = useCallback(
    (term?: string) => {
      setAppliedSearch((term ?? searchValue).trim());
      setPagination((prev) =>
        prev.current === 1 ? prev : { ...prev, current: 1 }
      );
    },
    [searchValue]
  );

  const handleFilterTypeChange = useCallback((value: CatalogFilterType) => {
    setSearchFilterType(value);
    setSearchValue("");
    setAppliedSearch("");
    setPagination((prev) =>
      prev.current === 1 ? prev : { ...prev, current: 1 }
    );
  }, []);

  useEffect(() => {
    fetchCatalog(
      catalogPage,
      catalogPageSize,
      appliedSearch,
      searchFilterType
    );
  }, [
    fetchCatalog,
    catalogPage,
    catalogPageSize,
    appliedSearch,
    searchFilterType,
  ]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: totalElements }));
  }, [totalElements]);

  const columnSelectionMenu = (
    <Menu>
      {parentColumnsDef.map((col) => (
        <Menu.Item key={col.key}>
          <Checkbox
            checked={selectedColumns.includes(col.key)}
            onChange={(e) => handleColumnSelection(col.key, e.target.checked)}
          >
            {col.title}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <>
      <div className="page__content">
        {syncResult && (
          <Alert
            className="catalog-page__sync-alert"
            type={syncResult.type}
            message={syncResult.message}
            showIcon
            closable
            onClose={clearSyncResult}
            style={{ marginBottom: 16 }}
            action={
              syncResult.logId ? (
                <Button size="small" type="link" onClick={handleViewSyncDetail}>
                  Ver detalle
                </Button>
              ) : undefined
            }
          />
        )}

        <div className="catalog-page__toolbar">
          <div className="catalog-page__toolbar-side catalog-page__toolbar-side--start">
            <div className="catalog-page__toolbar-group catalog-page__toolbar-group--actions">
              <span className="catalog-page__toolbar-label">Acciones</span>
              <div className="catalog-page__toolbar-control-row">
                <Button
                  type="primary"
                  onClick={openSyncModal}
                  loading={isBusy}
                >
                  {isBusy ? "Sincronizando" : "Sincronizar"}
                </Button>
                {renderSyncModal()}
                <ExportTableButton
                  data={exportData}
                  columns={exportColumns}
                  disabled={isBusy || exportData.length === 0}
                  variant="default"
                />
              </div>
            </div>
            <div
              className="catalog-page__toolbar-separator"
              aria-hidden="true"
            />
          </div>

          <div className="catalog-page__toolbar-middle">
            <div className="catalog-page__toolbar-field">
              <label
                htmlFor="catalog-filter-type"
                className="catalog-page__toolbar-label"
              >
                Buscar por
              </label>
              <Select
                id="catalog-filter-type"
                className="catalog-page__toolbar-filter-type"
                value={searchFilterType}
                options={CATALOG_FILTER_TYPE_OPTIONS}
                onChange={handleFilterTypeChange}
                disabled={isBusy}
              />
            </div>
            <div className="catalog-page__toolbar-field catalog-page__toolbar-field--search">
              <label
                htmlFor="catalog-search-value"
                className="catalog-page__toolbar-label"
              >
                Valor a buscar
              </label>
              <Input
                id="catalog-search-value"
                className="catalog-page__toolbar-search"
                placeholder={CATALOG_FILTER_TYPE_PLACEHOLDERS[searchFilterType]}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={() => submitSearch()}
                onClear={() => {
                  setSearchValue("");
                  submitSearch("");
                }}
                allowClear
                spellCheck={false}
                disabled={isBusy}
                suffix={
                  <button
                    type="button"
                    className="catalog-page__toolbar-search-btn"
                    aria-label="Buscar"
                    disabled={isBusy}
                    onClick={() => submitSearch()}
                  >
                    <SearchOutlined />
                  </button>
                }
              />
            </div>
          </div>

          <div className="catalog-page__toolbar-side catalog-page__toolbar-side--end">
            <div className="catalog-page__toolbar-group catalog-page__toolbar-group--columns">
              <span className="catalog-page__toolbar-label">
                Columnas a mostrar
              </span>
              <Dropdown
                overlay={columnSelectionMenu}
                trigger={["click"]}
                disabled={isBusy}
              >
                <Button
                  className="catalog-page__toolbar-columns-btn"
                  disabled={isBusy}
                >
                  Seleccionar Columnas <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {loading && catalogData.length === 0 ? (
          <TableSkeleton />
        ) : (
          <div className="data-enter">
            <Table<CatalogParentItem>
              className="catalog-page__table"
              columns={parentColumns}
              dataSource={catalogData}
              loading={{
                spinning: loading || syncLoading,
                tip: syncLoading
                  ? "Terminando de sincronizar..."
                  : undefined,
              }}
              rowKey="itemId"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No hay productos para los filtros aplicados"
                  />
                ),
              }}
              pagination={{
                ...pagination,
                ...TABLE_PAGINATION_UX,
                total: totalElements,
                showTotal: (total) =>
                  `${total.toLocaleString("es-CO")} ${total === 1 ? "producto" : "productos"}`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 960 }}
              expandable={{
                expandedRowRender: (record) => <VariationsTable record={record} />,
                rowExpandable: (record) =>
                  Array.isArray(record.variations) && record.variations.length > 0,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CatalogPage;
